// src/components/scenes/DeviceActionEditor.js
import React, { useState, useEffect } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import './DeviceActionEditor.css';

const DeviceActionEditor = ({ action, devices, onSave, onCancel, onDelete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [actionData, setActionData] = useState({
    type: 'device',
    device: '',
    payload: {},
    ...action
  });
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [customProperty, setCustomProperty] = useState('');
  const [customValue, setCustomValue] = useState('');
  
  // Find and set selected device when device ID changes
  useEffect(() => {
    if (actionData.device) {
      const device = devices.find(d => d.id === actionData.device);
      setSelectedDevice(device || null);
    }
  }, [actionData.device, devices]);
  
  // Handle device selection
  const handleDeviceChange = (e) => {
    const deviceId = e.target.value;
    const device = devices.find(d => d.id === deviceId);
    setActionData(prev => ({
      ...prev,
      device: deviceId,
      parentId: device.parentId || deviceId,
      payload: {} // Clear payload when device changes
    }));
  };
  
  // Get the main control property for this device
  const getMainControlProperty = () => {
    if (!selectedDevice) return 'state';
    
    if (selectedDevice.controls && selectedDevice.controls.length > 0) {
      // First try to find a property with "state" in the name
      const stateControl = selectedDevice.controls.find(c => 
        c.property && c.property.includes('state')
      );
      if (stateControl) return stateControl.property;
      
      // Otherwise just use the first control
      return selectedDevice.controls[0].property;
    }
    return 'state'; // Fallback to default
  };
  
  // Get control options
  const getDeviceControlInfo = () => {
    if (!selectedDevice) return { property: 'state', valueOn: 'ON', valueOff: 'OFF' };
    
    const property = getMainControlProperty();
    let valueOn = 'ON';
    let valueOff = 'OFF';
    
    if (selectedDevice.controls) {
      const control = selectedDevice.controls.find(c => c.property === property);
      if (control && control.options) {
        valueOn = control.options.on || 'ON';
        valueOff = control.options.off || 'OFF';
      }
    }
    
    return { property, valueOn, valueOff };
  };
  
  // Add common device commands
  const addCommonCommand = (command) => {
    const { property, valueOn, valueOff } = getDeviceControlInfo();
    let payload = {};
    
    switch (command) {
      case 'on':
        payload = { [property]: valueOn };
        break;
      case 'off':
        payload = { [property]: valueOff };
        break;
      case 'toggle':
        payload = { [property]: 'TOGGLE' };
        break;
      default:
        return;
    }
    
    setActionData(prev => ({
      ...prev,
      payload
    }));
  };
  
  // Handle adding a property-value pair to the payload
  const handleAddProperty = (property, value) => {
    let parsedValue = value;
    
    // Try to convert to number if it could be a number
    if (!isNaN(value) && value !== '') {
      parsedValue = Number(value);
    } else if (value === 'true') {
      parsedValue = true;
    } else if (value === 'false') {
      parsedValue = false;
    }
    
    setActionData(prev => ({
      ...prev,
      payload: {
        ...prev.payload,
        [property]: parsedValue
      }
    }));
  };
  
  // Add custom property to payload
  const handleAddCustomProperty = () => {
    if (!customProperty.trim()) return;
    
    handleAddProperty(customProperty, customValue);
    
    // Clear inputs
    setCustomProperty('');
    setCustomValue('');
  };
  
  // Remove property from payload
  const handleRemoveProperty = (property) => {
    setActionData(prev => {
      const newPayload = { ...prev.payload };
      delete newPayload[property];
      
      return {
        ...prev,
        payload: newPayload
      };
    });
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!actionData.device) {
      alert('Please select a device');
      return;
    }
    
    if (Object.keys(actionData.payload).length === 0) {
      alert('Please specify at least one property to control');
      return;
    }
    
    onSave(actionData);
  };

  // Handle delete action
  const handleDelete = () => {
    if (onDelete && typeof onDelete === 'function') {
      onDelete();
    }
  };
  
  // Render common controls for devices without specific controls
  const renderBasicControls = () => {
    return (
      <div className="basic-controls">
        <div className="command-buttons">
          <button 
            type="button" 
            className="command-btn on-btn"
            onClick={() => addCommonCommand('on')}
          >
            Turn On
          </button>
          <button 
            type="button" 
            className="command-btn off-btn"
            onClick={() => addCommonCommand('off')}
          >
            Turn Off
          </button>
          <button 
            type="button" 
            className="command-btn toggle-btn"
            onClick={() => addCommonCommand('toggle')}
          >
            Toggle
          </button>
        </div>
      </div>
    );
  };
  
  // Determine the value to use when adding a property to payload
  const getValueForProperty = (control) => {
    switch (control.valueType) {
      case 'binary':
        return control.options?.on || 'ON';
      case 'numeric':
        return control.value !== undefined ? control.value : (control.options?.max || 100);
      case 'enum':
        return control.value !== undefined ? control.value : 
          (control.options?.values && control.options.values.length ? control.options.values[0] : '');
      default:
        return control.value !== undefined ? control.value : '';
    }
  };
  
  // Render a control with an add button (based on DeviceCard renderControl)
  const renderControl = (control) => {
    if (!control || !control.property) return null;
    
    // Create an add button for the control
    const addButton = (
      <button 
        type="button"
        className="add-to-payload-btn"
        onClick={() => handleAddProperty(control.property, getValueForProperty(control))}
        title={`Add ${control.name || control.property} to payload`}
      >
        <FaPlus />
      </button>
    );
    
    switch (control.valueType) {
      case 'binary':
        const isOn = control.value === control.options?.on || control.value === true;
        return (
          <div className="control-item binary-control" key={control.property}>
            <span className="control-name">{control.name || control.property}</span>
            <div className="control-actions">
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={isOn}
                  
                />
                <span className="toggle-slider"></span>
              </label>
              {addButton}
            </div>
          </div>
        );
        
      case 'numeric':
        return (
          <div className="control-item numeric-control" key={control.property}>
            <div className="control-header">
              <span className="control-name">{control.name || control.property}</span>
              <span className="control-value">
                {control.value !== undefined ? control.value : 'N/A'}
                {control.options?.unit ? ` ${control.options.unit}` : ''}
              </span>
            </div>
            <div className="control-actions">
              <div className="slider-control">
                <input 
                  type="range" 
                  min={control.options?.min || 0} 
                  max={control.options?.max || 100} 
                  step={control.options?.step || 1}
                  value={control.value || 0}
                  
                />
              </div>
              {addButton}
            </div>
          </div>
        );
        
      case 'enum':
        return (
          <div className="control-item enum-control" key={control.property}>
            <span className="control-name">{control.name || control.property}</span>
            <div className="control-actions">
              <select
                value={control.value}
                
                className="enum-select"
              >
                {control.options?.values?.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {addButton}
            </div>
          </div>
        );
        
      default:
        return (
          <div className="control-item" key={control.property}>
            <span className="control-name">{control.name || control.property}</span>
            <div className="control-actions">
              <span className="control-value">
                {control.value !== undefined ? 
                  (typeof control.value === 'object' ? JSON.stringify(control.value) : control.value) : 
                  'N/A'}
                {control.options?.unit ? ` ${control.options.unit}` : ''}
              </span>
              {addButton}
            </div>
          </div>
        );
    }
  };
  
  // Render controls for a device - using the same approach as DeviceCard
  const renderDeviceControls = () => {
    if (!selectedDevice || !selectedDevice.controls || selectedDevice.controls.length === 0) {
      return renderBasicControls();
    }
    
    // Filter out controls that don't have a property
    const validControls = selectedDevice.controls.filter(control => control && control.property);
    
    return (
      <div className="device-controls">
        {validControls.map(control => renderControl(control))}
      </div>
    );
  };
  
  return (
    <div className="device-action-editor">
      <h2>{action ? 'Edit Device Action' : 'Add Device Action'}</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="device">Device</label>
          <select
            id="device"
            value={actionData.device}
            onChange={handleDeviceChange}
            required
          >
            <option value="">Select a device</option>
            {devices.map(device => (
              <option key={device.id} value={device.id}>
                {device.name || device.id} {device.location ? `(${device.location})` : ''}
              </option>
            ))}
          </select>
        </div>
        
        {selectedDevice && (
          <>
            <div className="device-info">
              <h3>Device Information</h3>
              <div className="device-details">
                <div><strong>ID:</strong> {selectedDevice.id}</div>
                {selectedDevice.type && <div><strong>Type:</strong> {selectedDevice.type}</div>}
                {selectedDevice.location && <div><strong>Location:</strong> {selectedDevice.location}</div>}
              </div>
            </div>
            
            <div className="device-controls-section">
              <h3>Device Controls</h3>
              {renderDeviceControls()}
            </div>
            
            <div className="custom-property">
              <h3>Custom Property</h3>
              
              <div className="custom-property-form">
                <div className="custom-prop-input">
                  <label htmlFor="customProperty">Property</label>
                  <input
                    type="text"
                    id="customProperty"
                    value={customProperty}
                    onChange={(e) => setCustomProperty(e.target.value)}
                    placeholder="Property name"
                  />
                </div>
                
                <div className="custom-value-input">
                  <label htmlFor="customValue">Value</label>
                  <input
                    type="text"
                    id="customValue"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    placeholder="Value"
                  />
                </div>
                
                <button
                  type="button"
                  className="add-property-btn"
                  onClick={handleAddCustomProperty}
                  disabled={!customProperty.trim()}
                >
                  <FaPlus />
                </button>
              </div>
            </div>
            
            <div className="current-payload">
              <h3>Payload</h3>
              
              {Object.keys(actionData.payload).length === 0 ? (
                <div className="empty-payload">
                  <p>No properties set. Add at least one property to control the device.</p>
                </div>
              ) : (
                <ul className="payload-list">
                  {Object.entries(actionData.payload).map(([property, value]) => (
                    <li key={property} className="payload-item">
                      <div className="payload-property">{property}</div>
                      <div className="payload-value">{JSON.stringify(value)}</div>
                      <button
                        type="button"
                        className="remove-property-btn"
                        onClick={() => handleRemoveProperty(property)}
                      >
                        <FaTimes />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
        
        <div className="editor-controls">
          {action && onDelete && (
            <button 
              type="button"
              className="delete-btn"
              onClick={handleDelete}
            >
              Delete Action
            </button>
          )}
          <div className="spacer"></div>
          <button 
            type="button"
            className="cancel-btn"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          
          <button 
            type="submit"
            className="save-btn"
            disabled={isLoading || !actionData.device || Object.keys(actionData.payload).length === 0}
          >
            {isLoading ? (
              <>
                <div className="spinner-sm"></div>
                <span>Saving...</span>
              </>
            ) : (
              <span>Save Action</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DeviceActionEditor;